/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-common
 * (C) Copyright IBM Corp. 2019, 2020
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { domUtils, classnames as cx, event, mathUtils, keyboardUtils } from '@ba-ui-toolkit/utils';
import Handle from './Handle';
import Track from './Track';
import styles from './Slider.scss';

const sliderClassName = styles.sliderClassName.replace(/"/g, '');

export default class Range extends Component {
	static propTypes = {
		values: PropTypes.arrayOf(PropTypes.number).isRequired,
		min: PropTypes.number.isRequired,
		max: PropTypes.number.isRequired,
		id: PropTypes.string,
		className: PropTypes.string,
		step: PropTypes.number.isRequired,
		showTooltip: PropTypes.bool,
		labelFormatter: PropTypes.func.isRequired,
		onChange: PropTypes.func.isRequired,
		onInputComplete: PropTypes.func.isRequired,
		onMouseEnter: PropTypes.func,
		onMouseLeave: PropTypes.func,
		onTouchStart: PropTypes.func,
		onTouchEnd: PropTypes.func,
		setFocus: PropTypes.func,
		setBlur: PropTypes.func,
		inverted: PropTypes.bool,
		disabled: PropTypes.bool,
	};

	constructor(props) {
		super(props);
		this._handleRefs = new Array(2);
	}

	state = {
		trackDragging: false,
		handleDragging: false,
	};

	componentWillUnmount() {
		this._removeEventListener();
	}

	_removeEventListener() {
		if (this.state.trackDragging) {
			window.removeEventListener('mousemove', this._onTrackDragging);
			window.removeEventListener('mouseup', this._onTrackDragDone);
			this._trackRef.removeEventListener('touchmove', this._onTrackDragging);
			this._trackRef.removeEventListener('touchend', this._onTrackDragDone);
		}
		if (this.state.handleDragging) {
			window.removeEventListener('mousemove', this._onHandleDragging);
			window.removeEventListener('mouseup', this._onHandleDragDone);
			this._handleRefs.forEach(handle => {
				handle.removeEventListener('touchmove', this._onHandleDragging);
				handle.removeEventListener('touchend', this._onHandleDragDone);
			});
		}
		this._trackRef.removeEventListener('mouseup', this._onTrackClick);
	}

	_toPercentage(value, min, max) {
		return Math.round(((value - min) / (max - min)) * 100);
	}

	_getMouseOrTouchPosition(e) {
		return e.pageX || e.touches[0].pageX;
	}

	_isTouchEvent(e) {
		return e.type === 'touchstart' || e.type === 'touchmove' || e.type === 'touchend';
	}

	_ensureValuesWithinRange(values, min, max) {
		return values.map(v => {
			if (v < min) {
				return min;
			} else if (v > max) {
				return max;
			}

			return v;
		});
	}

	_calculateValueOffset(offset) {
		const { step, min, max } = this.props;
		const { width } = this._getSliderPosition();
		const stepsTotal = Math.round((max - min) / step);
		const stepWidth = width / stepsTotal;

		return mathUtils.decimalMultiply(Math.round(offset / stepWidth), step);
	}

	_getSliderPosition() {
		return {
			left: domUtils.computeLeftWith(this._sliderRef),
			width: domUtils.computeWidthOf(this._sliderRef),
		};
	}

	_getKeyValueOffset(keyName, step) {
		const { LEFT, RIGHT, UP, DOWN } = keyboardUtils.keyNames;
		if (keyName === LEFT || keyName === DOWN) {
			return -step;
		} else if (keyName === RIGHT || keyName === UP) {
			return step;
		}

		return 0;
	}

	_moveClosestHandleToMouse = e => {
		if (this.props.disabled) return;

		const { values, onChange, min } = this.props;
		const { left } = this._getSliderPosition();
		const newValue = min + this._calculateValueOffset(this._getMouseOrTouchPosition(e) - left);
		const activeHandle = this._getClosestHandleIndex(values, newValue);
		values[activeHandle] = newValue;
		this._handleRefs[activeHandle].focus();
		onChange && onChange(values);
	};

	_getClosestHandleIndex(values, newValue) {
		if (values[0] !== values[1]) {
			return Math.abs(newValue - values[0]) < Math.abs(newValue - values[1]) ? 0 : 1;
		}

		return newValue - values[0] > 0 ? 1 : 0;
	}

	_onTrackDragStart = e => {
		if (this.props.disabled) return;

		event.stopEvent(e);

		this.setState({ trackDragging: true });
		this.prevTrackPos = this._getMouseOrTouchPosition(e);
		if (this._isTouchEvent(e)) {
			this._trackRef.addEventListener('touchend', this._onTrackDragDone);
			this._trackRef.addEventListener('touchmove', this._onTrackDragging);
		} else {
			this._trackRef.addEventListener('mouseup', this._moveClosestHandleToMouse);
			window.addEventListener('mouseup', this._onTrackDragDone);
			window.addEventListener('mousemove', this._onTrackDragging);
		}
	};

	_onTrackKeyDown = e => {
		if (this.props.disabled) return;

		const keyName = keyboardUtils.getKeyName(e);
		const { TAB } = keyboardUtils.keyNames;
		if (keyName === TAB) {
			return;
		}

		event.stopEvent(e);
		const { values, onChange, onInputComplete, step, min, max } = this.props;
		const offset = this._getKeyValueOffset(keyName, step);
		if ((offset < 0 && values[0] > min) || (offset > 0 && values[1] < max)) {
			onChange && onChange(values.map(value => mathUtils.decimalPlus(value, offset)));
			onInputComplete && onInputComplete();
		}
	};

	_onHandleKeyDown = (e, index) => {
		if (this.props.disabled) return;

		const keyName = keyboardUtils.getKeyName(e);
		const { TAB } = keyboardUtils.keyNames;
		if (keyName === TAB) {
			return;
		}

		event.stopEvent(e);
		const { values, onChange, onInputComplete, step, min, max } = this.props;
		const copyValues = [...values];
		const offset = this._getKeyValueOffset(keyName, step);
		copyValues[index] = mathUtils.decimalPlus(copyValues[index], offset);
		if (min <= copyValues[0] && copyValues[0] <= copyValues[1] && copyValues[1] <= max) {
			onChange && onChange(copyValues);
			onInputComplete && onInputComplete();
		}
	};

	_onTrackDragging = e => {
		if (this.props.disabled) return;

		event.stopEvent(e);

		const { values, onChange, min, max } = this.props;
		const mousePos = this._getMouseOrTouchPosition(e);
		const valueOffset = this._calculateValueOffset(mousePos - this.prevTrackPos);
		// Only update if we move mouse enough to have a change in value
		if (valueOffset !== 0) {
			this._trackRef.removeEventListener('mouseup', this._moveClosestHandleToMouse);
			this._trackRef.focus();
			this.prevTrackPos = mousePos;
			const postValues = values.map(v => mathUtils.decimalPlus(v, valueOffset));
			if (postValues[0] >= min && postValues[1] <= max) {
				onChange && onChange(postValues, min, max);
			}
		}
	};

	_onTrackDragDone = () => {
		this._removeEventListener();
		this.setState({ trackDragging: false });
		delete this.prevTrackPos;
		this.props.onInputComplete && this.props.onInputComplete();
	};

	_onHandleDragStart = (e, index) => {
		if (this.props.disabled) return;

		event.stopEvent(e);

		this._handleRefs[index].focus();
		this.setState({ handleDragging: true });
		this.activeHandleIndex = index;
		if (this._isTouchEvent(e)) {
			this._handleRefs[index].addEventListener('touchend', this._onHandleDragDone);
			this._handleRefs[index].addEventListener('touchmove', this._onHandleDragging);
		} else {
			window.addEventListener('mouseup', this._onHandleDragDone);
			window.addEventListener('mousemove', this._onHandleDragging);
		}
	};

	_onHandleDragging = e => {
		if (this.props.disabled) return;

		event.stopEvent(e);

		const { values, onChange, min, max } = this.props;
		const { left } = this._getSliderPosition();
		const newValue = min + this._calculateValueOffset(this._getMouseOrTouchPosition(e) - left);
		if (this.activeHandleIndex === 0) {
			values[0] = newValue > values[1] ? values[1] : newValue;
		} else {
			values[1] = newValue < values[0] ? values[0] : newValue;
		}
		onChange && onChange(this._ensureValuesWithinRange(values, min, max));
	};

	_onHandleDragDone = () => {
		this._removeEventListener();
		this.setState({ handleDragging: false });
		delete this.activeHandleIndex;
		this.props.onInputComplete && this.props.onInputComplete();
	};

	_onRailClick = e => {
		if (this.props.disabled) return;

		const { values, min } = this.props;
		const { left } = this._getSliderPosition();
		const newValue = min + this._calculateValueOffset(this._getMouseOrTouchPosition(e) - left);
		const activeHandle = this._getClosestHandleIndex(values, newValue);

		this._moveClosestHandleToMouse(e);
		this._onHandleDragStart(e, activeHandle);
	};

	render() {
		const {
			values,
			min,
			max,
			className,
			onMouseEnter,
			onMouseLeave,
			showTooltip,
			labelFormatter,
			setFocus,
			setBlur,
			inverted,
		} = this.props;
		const { trackDragging, handleDragging } = this.state;

		const containerProps = {
			className: `${className}`,
			ref: node => {
				this._sliderRef = node;
			},
			onMouseDown: this._onRailClick,
			// Need to stop touch event to avoid triggering mouse simulation events
			onTouchStart: e => event.stopEvent(e),
			onTouchEnd: e => event.stopEvent(e),
		};

		const valuesAsPercent = values.map(value => this._toPercentage(value, min, max));

		const railProps = {
			className: `${sliderClassName}__rail${inverted ? ' is-inverted' : ''}`,
		};

		const trackProps = {
			trackRef: node => {
				this._trackRef = node;
			},
			values,
			labelFormatter,
			showTooltip: showTooltip && !this.state.handleDragging,
			style: {
				left: `${valuesAsPercent[0]}%`,
				width: `${valuesAsPercent[1] - valuesAsPercent[0]}%`,
			},
			onMouseDown: this._onTrackDragStart,
			onTouchStart: this._onTrackDragStart,
			onKeyDown: this._onTrackKeyDown,
			setFocus,
			setBlur,
			inverted,
		};

		const handlesProps = valuesAsPercent.map((value, index) => ({
			className: cx(`handle-${index}`),
			handleRef: node => {
				this._handleRefs[index] = node;
			},
			style: {
				left: `${value}%`,
			},
			value: values[index],
			showTooltip:
				showTooltip &&
				!trackDragging &&
				(!handleDragging || this.activeHandleIndex === index),
			labelFormatter,
			onMouseDown: e => this._onHandleDragStart(e, index),
			onTouchStart: e => {
				e.preventDefault();
				this._onHandleDragStart(e, index);
			},
			onKeyDown: e => this._onHandleKeyDown(e, index),
			onMouseEnter,
			onMouseLeave,
			inverted,
		}));

		const stepProps = {
			className: `${sliderClassName}__step`,
		};

		return (
			<div {...containerProps}>
				<div {...railProps} />
				<div {...stepProps}>
					<Track {...trackProps} />
					<Handle {...handlesProps[0]} />
					<Handle {...handlesProps[1]} />
				</div>
			</div>
		);
	}
}
